import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useDownloadInsurancePolicy,
  useInsurancePolicy,
  useInsurancePolicyPdf,
  useReclassifyReviewThread,
} from "@/hooks/cle-hooks";
import type { InsurancePolicyModel } from "@/api/cle-api";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import {
  getReclassifyLocks,
  getMissingPolicyFields,
} from "../person/id/Efteruddannelse/utils/approvalCompleteness";
import { DocumentClassificationActions } from "../person/id/Efteruddannelse/DocumentClassificationActions";
import { EvidencePdfViewer } from "../person/id/Efteruddannelse/EvidencePdfViewer";
import type { CourseCertificateDecision } from "../person/id/Efteruddannelse/types";
import { useObjectUrl } from "../person/id/Efteruddannelse/useObjectUrl";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { InsurancePolicyInformation } from "./InsurancePolicyInformation";
import { InsurancePolicyValidation } from "./InsurancePolicyValidation";
import { ReviewDetailLayout } from "./ReviewDetailLayout";

interface InsurancePolicyReviewViewProps {
  userId: string;
  insurancePolicyId: string;
  onBack: () => void;
}

export const InsurancePolicyReviewView = ({
  userId,
  insurancePolicyId,
  onBack,
}: InsurancePolicyReviewViewProps) => {
  const { data, isLoading, error } = useInsurancePolicy({
    userId,
    insurancePolicyId,
  });
  const { data: pdfBlob, isLoading: isPdfLoading } = useInsurancePolicyPdf({
    userId,
    insurancePolicyId,
  });
  const toaster = useToaster();
  const { mutate: downloadInsurancePolicy, isPending: isDownloading } =
    useDownloadInsurancePolicy();
  const { mutate: reclassifyPolicy, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [policyOverride, setPolicyOverride] = useState<
    InsurancePolicyModel | undefined
  >(undefined);

  useEffect(() => {
    setPolicyOverride(undefined);
  }, [insurancePolicyId]);

  const policy = useMemo(
    () => mergeEvidenceDetailWithOverride(data, policyOverride),
    [data, policyOverride],
  );

  const handlePolicyUpdated = (updated: InsurancePolicyModel) => {
    setPolicyOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyPolicy(
      {
        subjectId: insurancePolicyId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setPolicyOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={policy?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente forsikringspolice."
          />
        ) : null
      }
      pdfPanel={
        policy ? (
          <EvidencePdfViewer
            fileName={policy.fileName ?? "Forsikringspolice"}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={() =>
              downloadInsurancePolicy({ userId, insurancePolicyId })
            }
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={insurancePolicyId} />}
      infoPanel={
        policy ? (
          <>
            <InsurancePolicyInformation policy={policy} />
            <InsurancePolicyValidation
              policy={policy}
              onPolicyUpdated={handlePolicyUpdated}
            />
          </>
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={insurancePolicyId}
          ownerUserId={userId}
          onCommentAdded={() => setPolicyOverride(undefined)}
        />
      }
      actionsPanel={
        policy ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              // A policy is not reportable evidence, so it has no reported lock to honour.
              isReported: false,
              missingFields: getMissingPolicyFields(policy),
              reportedReason: "",
            })}
          />
        ) : null
      }
    />
  );
};
